import type { CalendarDay } from '@/api/calendar'
import type { RecurringItem } from '@/api/recurring'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { cn } from '@/lib/utils'
import { shade, type Shade } from './calendarModel'
import { approxAmount } from '@/lib/recurring'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Five steps along the brand green; the deepest two switch to light text.
const SHADES: Record<Shade, string> = {
  0: 'bg-[var(--fl-surface)] border-[var(--fl-divider)]',
  1: 'bg-[color-mix(in_srgb,var(--fl-hero-bar)_12%,var(--fl-surface))] border-transparent',
  2: 'bg-[color-mix(in_srgb,var(--fl-hero-bar)_28%,var(--fl-surface))] border-transparent',
  3: 'bg-[color-mix(in_srgb,var(--fl-hero-bar)_48%,var(--fl-surface))] border-transparent',
  4: 'bg-[color-mix(in_srgb,var(--fl-hero-bar)_75%,var(--fl-surface))] border-transparent',
  5: 'bg-[var(--fl-primary)] border-transparent text-[var(--fl-primary-fg)]',
}

export function MonthGrid({
  month,
  days,
  dueByDate,
  selected,
  today,
  compact,
  onSelect,
}: {
  month: string
  days: CalendarDay[]
  dueByDate: Map<string, RecurringItem[]>
  selected: string | null
  today: string
  compact: boolean
  onSelect: (date: string) => void
}) {
  const { format, formatCompact } = useFormatCurrency()
  const [y, m] = month.split('-').map(Number)
  const daysInMonth = new Date(y, m, 0).getDate()
  const blanks = new Date(y, m - 1, 1).getDay()
  const byDate = new Map(days.map((d) => [d.date, d]))
  const max = Math.max(0, ...days.map((d) => d.expenseTotal))

  return (
    <div>
      <div className="mb-1 grid grid-cols-7 gap-[3px] text-center text-[11px] text-[var(--fl-text-3)] md:gap-1.5">
        {WEEKDAYS.map((d) => (
          <span key={d}>{compact ? d[0] : d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-[3px] md:gap-1.5">
        {Array.from({ length: blanks }, (_, i) => (
          <div key={`b${i}`} />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const date = `${month}-${String(i + 1).padStart(2, '0')}`
          const agg = byDate.get(date)
          const future = date > today
          const due = future ? (dueByDate.get(date) ?? []) : []
          const s = future ? 0 : shade(agg?.expenseTotal ?? 0, max)
          const label = [
            date,
            agg && agg.expenseTotal > 0 && `spent ${format(agg.expenseTotal)}`,
            agg && agg.incomeTotal > 0 && `income ${format(agg.incomeTotal)}`,
            due.length > 0 &&
              `due: ${due.map((d) => `${d.name} ${approxAmount(d, format(d.amount))}`).join(', ')}`,
          ]
            .filter(Boolean)
            .join(', ')
          return (
            <button
              key={date}
              type="button"
              aria-label={label}
              aria-pressed={selected === date}
              data-shade={s}
              data-future={future || undefined}
              onClick={() => onSelect(date)}
              className={cn(
                'relative flex flex-col items-start rounded-[10px] border p-1 text-left text-xs transition-shadow md:min-h-[78px] md:p-1.5',
                compact ? 'min-h-[42px]' : 'min-h-[78px]',
                future
                  ? 'border-dashed border-[var(--fl-border)] bg-[var(--fl-surface)]'
                  : SHADES[s],
                date === today && 'outline outline-2 -outline-offset-2 outline-[var(--fl-hero-fg)]',
                selected === date &&
                  'outline outline-2 -outline-offset-2 outline-[var(--fl-primary)] shadow-[var(--card-shadow-sm)]'
              )}
            >
              <span className={cn('font-semibold', future && 'text-[var(--fl-text-3)]')}>
                {i + 1}
              </span>
              {agg && agg.incomeTotal > 0 && !future && (
                <span
                  aria-hidden="true"
                  className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-[var(--fl-income)] md:h-[7px] md:w-[7px]"
                />
              )}
              {!compact && !future && agg && agg.expenseTotal > 0 && (
                <span className="absolute bottom-1.5 left-1.5 text-[11px] font-semibold tabular-nums">
                  {formatCompact(agg.expenseTotal)}
                </span>
              )}
              {due.length > 0 &&
                (compact ? (
                  <span
                    aria-hidden="true"
                    className="absolute bottom-1 left-1 h-1.5 w-1.5 rounded-full bg-[var(--fl-warning)]"
                  />
                ) : (
                  <span className="absolute inset-x-1.5 bottom-1.5 truncate rounded-[5px] bg-[var(--fl-warning-bg)] px-1 text-[10.5px] font-semibold text-[var(--fl-warning-text)]">
                    {due[0].name} {approxAmount(due[0], formatCompact(due[0].amount))}
                    {due.length > 1 ? ` +${due.length - 1}` : ''}
                  </span>
                ))}
            </button>
          )
        })}
      </div>
    </div>
  )
}
